import { useCallback } from 'react';
import { uniqBy } from 'lodash';

import useRequestJSON, {
  IsLoadingSegment,
  RunnerSegment
} from './hooks/useRequestJSON';
import {
  PostJSON,
  ErrorResponse,
  BaseResponse,
  PaginatedParams
} from './request';

export enum TagType {
  Contact = 'c',
  Manager = 'm',
  Practice = 'pc',
  Patient = 'pt'
}

export interface ITag {
  id: number;
  type?: TagType;
  value: string;
  description?: string;
  createdAt: Date;
  updatedAt?: Date;
  deletedAt?: Date;
}

// Utils

export interface GetTagParams {
  id?: number;
  ids?: number[];
}

export interface GetTagsParams extends PaginatedParams {
  keyword?: string;
  type?: TagType;
  list?: boolean;
}

export interface GetTagResponse extends BaseResponse {
  tag?: ITag;
  tags?: ITag[];
}

export interface GetTagsResponse extends BaseResponse {
  tags: ITag[];
  total: number;
  readOnly: boolean;
}

interface GetTagFn {
  (params: GetTagParams): Promise<GetTagResponse | ErrorResponse>;
}

interface GetTagsFn {
  (params: GetTagsParams): Promise<GetTagsResponse | ErrorResponse>;
}

export const GetTag: GetTagFn = (params) =>
  PostJSON<GetTagResponse, GetTagParams>({
    url: '/tag/data_single/',
    params
  });

export const GetTags: GetTagsFn = (params) =>
  PostJSON<GetTagsResponse, GetTagsParams>({
    url: '/tag/data/',
    params
  });

interface GetAllTagsParams {
  type?: TagType;
  accountId?: number;
}

interface GetAllTagsResponse extends BaseResponse {
  tags: ITag[];
}

export const GetAllTags: GetTagsFn = (params) =>
  PostJSON<GetTagsResponse, GetAllTagsParams>({
    url: '/tag/list/',
    params
  });

interface MatchTagsParams {
  values: string[];
  type?: TagType;
}

interface MatchTagsFn {
  (params: MatchTagsParams): Promise<GetTagsResponse | ErrorResponse>;
}

export const MatchTags: MatchTagsFn = (params) =>
  PostJSON<GetTagsResponse, MatchTagsParams>({
    url: '/tag/fetch_by_values/',
    params: params
  });

export const transformTag = (
  response: GetTagResponse
): Omit<GetTagResponse, 'status'> => {
  return {
    tags: response.tags,
    tag: response.tag
  };
};

export const transformTags = (
  response: GetTagsResponse
): Omit<GetTagsResponse, 'status'> => {
  let tags = response.tags;
  if (tags) {
    tags = uniqBy(tags, (tag) => tag.value.trim());
  }
  return {
    tags,
    total: response.total,
    readOnly: response.readOnly
  };
};

// Hooks

export const useGetTag = (
  params: GetTagParams
): Omit<GetTagResponse, 'status'> => {
  const request = useCallback(() => GetTag(params), [params.id, params.ids]);
  const { data } = useRequestJSON<
    GetTagResponse,
    Omit<GetTagResponse, 'status'>
  >({
    request,
    transformFn: transformTag
  });
  return data;
};

export const useGetTags = (
  params?: GetTagsParams,
  isEnabled = true
): Omit<GetTagsResponse, 'status'> & RunnerSegment & IsLoadingSegment => {
  params = params || {
    page: 0,
    limit: 10
  };
  const request = useCallback(
    () => GetTags(params),
    [params.page, params.limit, params.keyword, params.type, params.list]
  );
  const { data, run, isLoading } = useRequestJSON<
    GetTagsResponse,
    Omit<GetTagsResponse, 'status'>
  >({
    request,
    transformFn: transformTags,
    enabled: isEnabled
  });
  return {
    tags: data?.tags || [],
    total: data?.total,
    readOnly: data?.readOnly,
    message: data?.message,
    isLoading,
    run: () => run(request)
  };
};

export const useGetAllTags = (
  params?: GetAllTagsParams
): Omit<GetAllTagsResponse, 'status'> => {
  const request = useCallback(() => GetAllTags(params), [params.type]);
  const { data } = useRequestJSON<
    GetTagsResponse,
    Omit<GetAllTagsResponse, 'status'>
  >({
    request,
    transformFn: transformTags
  });
  return {
    tags: data?.tags || []
  };
};

// Requests

export const GetTagsByValue = async (tags: string): Promise<ITag[]> => {
  if (!tags || tags.trim().length === 0) {
    return [];
  }
  const response = await MatchTags({
    values: tags.split(','),
    type: TagType.Patient
  });
  if (response.status !== 'OK') {
    return [];
  }
  return (response as GetTagsResponse).tags;
};

export interface TagParams {
  type: TagType;
  value: string;
  description?: string;
}

export interface TagResponse extends BaseResponse {
  tag: ITag;
}

export const CreateTag = (
  params: TagParams
): Promise<TagResponse | ErrorResponse> => {
  return PostJSON<BaseResponse>({
    url: '/tag/create/',
    params: params
  });
};

export const EditTag = (
  params: Omit<TagParams, 'type'> & { id: number }
): Promise<TagResponse | ErrorResponse> => {
  return PostJSON<BaseResponse>({
    url: '/tag/edit/',
    params: params
  });
};

export const RemoveTag = (params: {
  id: number;
}): Promise<BaseResponse | ErrorResponse> => {
  return PostJSON<BaseResponse>({
    url: '/tag/remove/',
    params: params
  });
};
